import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useActingOrg } from '@/auth/actingOrgContext';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { Drawer, useToast } from '@/components/ui';
import {
    assignConsoleRole,
    fetchRoleAssignments,
    fetchUserGroups,
    revokeConsoleRoleAssignment,
    type ConsoleRole,
} from '@/api/orgAdmin';
import { SearchIcon } from './icons';

export interface AssignRoleDrawerProps {
    /** `null` closes the drawer. */
    role: ConsoleRole | null;
    onClose: () => void;
}

type Target = 'group' | 'person';
type Scope = 'org' | 'system';

/**
 * EAA-1843: "Gán vai trò" — frame 64, the slide-over on the Vai trò screen.
 *
 * Two axes, exactly as the frame draws them: WHO (a user group, or one person)
 * and WHERE (inside one organization, or system-wide).
 *
 * The scope radio is not cosmetic. `organization: null` means the grant applies
 * everywhere, which for a role holding org-manage permissions is a materially
 * different act from granting it inside one org — so it is an explicit choice
 * rather than a default derived from the current picker.
 *
 * SCOPE ORG AND GROUP ORG ARE SEPARATE, which the frame states out loud: "Phạm
 * vi quyết định vai trò có hiệu lực ở đâu, độc lập với tổ chức của nhóm." A group
 * belongs to the acting org; where the role it receives takes effect is its own
 * decision, so the scope select is a real control and not a readout of the org
 * picker. The group list stays on the acting org — that is the only org whose
 * groups the console has loaded.
 *
 * Existing assignments are listed with a revoke. Frame 64 does not draw that
 * section, but the capability already shipped and the realistic use of this
 * drawer is adjusting a grant rather than making a first one — dropping it to
 * match a mock would push revocation back into the API.
 */
export function AssignRoleDrawer({ role, onClose }: AssignRoleDrawerProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const acting = useActingOrg();
    const toast = useToast();
    const queryClient = useQueryClient();
    const orgId = acting?.actingOrgId ?? null;
    const orgs = useMemo(() => acting?.organizations ?? [], [acting]);

    const [target, setTarget] = useState<Target>('group');
    const [scope, setScope] = useState<Scope>('org');
    const [scopeOrgId, setScopeOrgId] = useState<string | null>(null);
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [userSub, setUserSub] = useState('');
    const [search, setSearch] = useState('');
    const [showPeople, setShowPeople] = useState(false);

    const open = Boolean(role);
    // Defaults to the acting org but is not bound to it — see the class docstring.
    const effectiveScopeOrgId = scopeOrgId ?? orgId;
    const scopeOrgName =
        orgs.find((o) => o.id === effectiveScopeOrgId)?.name ?? acting?.actingOrg?.name ?? '';
    const actingOrgName = acting?.actingOrg?.name ?? t('console.permissions.assignDrawer.actingOrgFallback');

    const groupsQuery = useQuery({
        queryKey: ['user-groups', accessToken, orgId],
        queryFn: () => fetchUserGroups(config.apiBase, accessToken as string, orgId as string),
        enabled: open && Boolean(orgId && accessToken),
        retry: false,
    });

    const assignmentsQuery = useQuery({
        queryKey: ['role-assignments', accessToken, role?.id],
        queryFn: () => fetchRoleAssignments(config.apiBase, accessToken as string, role!.id as string),
        enabled: open && Boolean(role?.id && accessToken),
        retry: false,
    });

    // Memoized for the same reason as RolesTab: a fresh array each render would
    // make every derived count recompute on each keystroke elsewhere in the drawer.
    const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

    const visibleGroups = useMemo(() => {
        const term = search.trim().toLowerCase();
        // Deactivated groups are not offered: granting to one would be a grant
        // that quietly reaches nobody until someone reactivates it.
        const usable = groups.filter((g) => g.is_active || picked.has(g.id));
        return term ? usable.filter((g) => g.name.toLowerCase().includes(term)) : usable;
    }, [groups, search, picked]);

    /**
     * Distinct people, not the sum of member counts — someone in two selected
     * groups receives the role once, and "20 người" that double-counts them is a
     * number an operator will later find wrong.
     */
    const reachSubs = useMemo(() => {
        const subs = new Set<string>();
        for (const g of groups) {
            if (!picked.has(g.id)) continue;
            for (const m of g.members) subs.add(m.user_sub);
        }
        return subs;
    }, [groups, picked]);

    const people = useMemo(() => {
        const bySub = new Map<string, string>();
        for (const g of groups) {
            if (!picked.has(g.id)) continue;
            for (const m of g.members) if (!bySub.has(m.user_sub)) bySub.set(m.user_sub, m.user_email);
        }
        return [...bySub].map(([sub, email]) => ({ sub, email }));
    }, [groups, picked]);

    function invalidate() {
        void queryClient.invalidateQueries({ queryKey: ['role-assignments', accessToken, role?.id] });
        void queryClient.invalidateQueries({ queryKey: ['console-roles', accessToken] });
        void queryClient.invalidateQueries({ queryKey: ['user-groups', accessToken, orgId] });
    }

    const assign = useMutation({
        mutationFn: async () => {
            const organization = scope === 'org' ? effectiveScopeOrgId : null;
            if (target === 'person') {
                await assignConsoleRole(config.apiBase, accessToken as string, role!.id as string, {
                    user_sub: userSub.trim(),
                    organization,
                });
                return 1;
            }
            // One call per group: the API grants a single target per row, which is
            // what makes each grant independently revocable.
            for (const gid of picked) {
                await assignConsoleRole(config.apiBase, accessToken as string, role!.id as string, {
                    group: gid,
                    organization,
                });
            }
            return picked.size;
        },
        onSuccess: (n) => {
            // EAA-1845: person target always assigns exactly 1 (see mutationFn
            // above), so the person-branch toast never needs plural handling;
            // the group branch's count is real and goes through i18next's
            // _one/_other suffixes instead of a hand-rolled ternary.
            if (target === 'person') {
                toast.success(t('console.permissions.assignDrawer.assignedPersonToast', { count: n }));
            } else {
                toast.success(t('console.permissions.assignDrawer.assignedGroupToast', { count: n }));
            }
            setPicked(new Set());
            setUserSub('');
            setShowPeople(false);
            invalidate();
        },
        onError: (e: unknown) =>
            toast.error(
                e instanceof Error ? e.message : t('console.permissions.assignDrawer.assignErrorToast')
            ),
    });

    const revoke = useMutation({
        mutationFn: (assignmentId: string) =>
            revokeConsoleRoleAssignment(
                config.apiBase,
                accessToken as string,
                role!.id as string,
                assignmentId
            ),
        onSuccess: () => {
            toast.success(t('console.permissions.assignDrawer.revokedToast'));
            invalidate();
        },
    });

    const canSubmit = target === 'person' ? userSub.trim().length > 0 : picked.size > 0;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={560}
            closable={false}
            className="assign-drawer"
            title={
                <span className="assign-head">
                    <span className="assign-head-title">
                        {t('console.permissions.assignDrawer.title')}
                    </span>
                    {role && (
                        <span className="assign-head-sub">
                            {t('console.permissions.assignDrawer.headSubRole')}{' '}
                            <strong>{role.name}</strong> · {role.permissions.length}{' '}
                            {t('console.permissions.assignDrawer.headSubPermissions')}
                        </span>
                    )}
                </span>
            }
            extra={
                <button
                    type="button"
                    className="assign-close"
                    onClick={onClose}
                    aria-label={t('common.close')}
                >
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
                         stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
                    </svg>
                </button>
            }
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.permissions.assignDrawer.footerNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        <button type="button" className="portal-btn" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            disabled={!canSubmit || assign.isPending}
                            onClick={() => assign.mutate()}
                        >
                            {assign.isPending
                                ? t('console.permissions.assignDrawer.submitPending')
                                : target === 'person'
                                  ? t('console.permissions.assignDrawer.submitPerson')
                                  : t('console.permissions.assignDrawer.submitGroups', {
                                        count: picked.size,
                                    })}
                        </button>
                    </span>
                </div>
            }
        >
            <section className="assign-block">
                <p className="assign-label">{t('console.permissions.assignDrawer.targetLabel')}</p>
                <div className="assign-seg">
                    <button
                        type="button"
                        className={target === 'group' ? 'assign-seg-on' : ''}
                        onClick={() => setTarget('group')}
                    >
                        {t('console.permissions.assignDrawer.targetGroupOption')}
                    </button>
                    <button
                        type="button"
                        className={target === 'person' ? 'assign-seg-on' : ''}
                        onClick={() => setTarget('person')}
                    >
                        {t('console.permissions.assignDrawer.targetPersonOption')}
                    </button>
                </div>
                <p className="assign-hint">
                    {t('console.permissions.assignDrawer.hintTargetPrefix')}{' '}
                    <code>sub</code> {t('console.permissions.assignDrawer.hintTargetSuffix')}
                </p>
            </section>

            <section className="assign-block">
                <p className="assign-label">{t('console.permissions.assignDrawer.scopeLabel')}</p>
                <label className="assign-radio">
                    <input
                        type="radio"
                        checked={scope === 'org'}
                        onChange={() => setScope('org')}
                        disabled={!effectiveScopeOrgId}
                    />
                    {t('console.permissions.assignDrawer.scopeOrgOption')}
                </label>
                <label className="assign-radio">
                    <input
                        type="radio"
                        checked={scope === 'system'}
                        onChange={() => setScope('system')}
                    />
                    {t('console.permissions.assignDrawer.scopeSystemOption')}
                </label>
                {scope === 'org' && (
                    <div className="assign-orgpick">
                        <span className="assign-orgpick-tile" aria-hidden="true">
                            {initials(scopeOrgName)}
                        </span>
                        <span className="assign-orgpick-name">{scopeOrgName || '—'}</span>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                             stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {/* Transparent native select over the visible pill — same
                            technique as ActingOrgPicker, no dropdown dependency. */}
                        <select
                            className="assign-orgpick-select"
                            value={effectiveScopeOrgId ?? ''}
                            onChange={(e) => setScopeOrgId(e.target.value)}
                            aria-label={t('console.permissions.assignDrawer.scopeOrgSelectAriaLabel')}
                        >
                            {orgs.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <p className="assign-hint">
                    {t('console.permissions.assignDrawer.scopeHint')}
                </p>
            </section>

            {target === 'person' ? (
                <section className="assign-block">
                    <p className="assign-label">{t('console.permissions.assignDrawer.personSectionLabel')}</p>
                    <input
                        type="text"
                        className="assign-input"
                        placeholder={t('console.permissions.assignDrawer.personInputPlaceholder')}
                        value={userSub}
                        onChange={(e) => setUserSub(e.target.value)}
                    />
                </section>
            ) : (
                <section className="assign-block">
                    <p className="assign-label assign-label--row">
                        {t('console.permissions.assignDrawer.groupSectionLabel')}
                        <span className="assign-count">
                            {t('console.permissions.assignDrawer.groupCount', {
                                picked: picked.size,
                                reach: reachSubs.size,
                            })}
                        </span>
                    </p>
                    <label className="perm-search assign-search">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('console.permissions.assignDrawer.searchGroupsPlaceholder', {
                                org: actingOrgName,
                            })}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label={t('console.permissions.assignDrawer.searchGroupsAriaLabel')}
                        />
                    </label>
                    {groupsQuery.isLoading && (
                        <p className="assign-hint">
                            {t('console.permissions.assignDrawer.loadingGroups')}
                        </p>
                    )}
                    {groupsQuery.isSuccess && groups.length === 0 && (
                        <p className="assign-hint">
                            {t('console.permissions.assignDrawer.noGroupsHint')}
                        </p>
                    )}
                    {groupsQuery.isSuccess && groups.length > 0 && visibleGroups.length === 0 && (
                        <p className="assign-hint">
                            {t('console.permissions.assignDrawer.noGroupsMatch')}
                        </p>
                    )}
                    <div className="assign-grouplist">
                        {visibleGroups.map((g) => (
                            <label key={g.id} className="assign-group">
                                <input
                                    type="checkbox"
                                    checked={picked.has(g.id)}
                                    onChange={() =>
                                        setPicked((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(g.id)) next.delete(g.id);
                                            else next.add(g.id);
                                            return next;
                                        })
                                    }
                                />
                                <span className="assign-group-body">
                                    <span className="assign-group-name">{g.name}</span>
                                    <span className="assign-group-meta">{actingOrgName}</span>
                                </span>
                                <span className="assign-group-n">
                                    {t('console.permissions.assignDrawer.groupMemberCount', {
                                        count: g.member_count,
                                    })}
                                </span>
                            </label>
                        ))}
                    </div>
                </section>
            )}

            <section className="assign-block">
                <p className="assign-label">{t('console.permissions.assignDrawer.currentAssignmentsLabel')}</p>
                {assignmentsQuery.isSuccess && assignmentsQuery.data.length === 0 && (
                    <p className="assign-hint">{t('console.permissions.assignDrawer.noAssignments')}</p>
                )}
                {(assignmentsQuery.data ?? []).map((a) => (
                    <div key={a.id} className="assign-existing">
                        <span className="assign-group-body">
                            <span className="assign-group-name">{a.group_name ?? a.user_sub}</span>
                            <span className="assign-group-meta">
                                {a.organization
                                    ? t('console.permissions.assignDrawer.assignmentScopeOrg')
                                    : t('console.permissions.assignDrawer.scopeSystemInline')}
                            </span>
                        </span>
                        <button
                            type="button"
                            className="portal-btn portal-btn-link"
                            disabled={revoke.isPending}
                            onClick={() => revoke.mutate(a.id)}
                        >
                            {t('console.permissions.assignDrawer.revokeButton')}
                        </button>
                    </div>
                ))}
            </section>

            {/* Frame 64's "Sau khi lưu" — the one place that says what pressing the
                button will actually do. "Quyền được cộng thêm" is the sentence that
                matters: an operator's real fear here is demoting someone. */}
            <section className="assign-after">
                <p className="assign-after-title">
                    {t('console.permissions.assignDrawer.afterSaveTitle')}
                </p>
                <p className="assign-after-body">
                    {target === 'person' ? (
                        <>
                            {t('console.permissions.assignDrawer.afterSaveAppliesTo')}{' '}
                            <strong>{t('console.permissions.assignDrawer.afterSavePersonCount')}</strong>
                            {t('console.permissions.assignDrawer.afterSaveScopeConnector')}{' '}
                            {scope === 'org'
                                ? scopeOrgName
                                : t('console.permissions.assignDrawer.scopeSystemInline')}
                            .
                        </>
                    ) : (
                        <>
                            {t('console.permissions.assignDrawer.afterSaveAppliesTo')}{' '}
                            <strong>
                                {t('console.permissions.assignDrawer.afterSaveGroupCountUnit', {
                                    count: reachSubs.size,
                                })}
                            </strong>{' '}
                            {t('console.permissions.assignDrawer.afterSaveGroupIn')}{' '}
                            {picked.size} {t('console.permissions.assignDrawer.afterSaveGroupUnit')}{' '}
                            {scope === 'org'
                                ? scopeOrgName
                                : t('console.permissions.assignDrawer.scopeSystemInline')}
                            .
                        </>
                    )}{' '}
                    {t('console.permissions.assignDrawer.afterSaveFooter')}
                </p>
                {/* The frame's "Xem N người sẽ nhận vai trò" is a real list here, not
                    a placeholder: the group payload already carries its roster, so
                    the names cost no extra request. */}
                {target === 'group' && reachSubs.size > 0 && (
                    <>
                        <button
                            type="button"
                            className="portal-btn portal-btn-link assign-after-link"
                            aria-expanded={showPeople}
                            onClick={() => setShowPeople((v) => !v)}
                        >
                            {showPeople
                                ? t('console.permissions.assignDrawer.viewPeopleToggleHide')
                                : t('console.permissions.assignDrawer.viewPeopleToggleShow', {
                                      count: reachSubs.size,
                                  })}
                        </button>
                        {showPeople && (
                            <ul className="assign-people">
                                {people.map((p) => (
                                    <li key={p.sub}>{p.email || p.sub}</li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </section>
        </Drawer>
    );
}

/** Same derivation as ActingOrgPicker's tile — first letters of the first two words. */
function initials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

export default AssignRoleDrawer;
